import { useEffect, useMemo, useState } from 'react';

const defaultRow = (id) => ({
    id,
    itemType: 'ingredient',
    itemId: '',
    quantity: '',
    notes: '',
});

export default function StockOutputTab({ ingredients = [], consumables = [], onCreateStockOutput }) {
    const [movementDate, setMovementDate] = useState(new Date().toISOString().slice(0, 10));
    const [service, setService] = useState('cuisine');
    const [rows, setRows] = useState([defaultRow(1)]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [historyEntries, setHistoryEntries] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [historyDateFrom, setHistoryDateFrom] = useState('');
    const [historyDateTo, setHistoryDateTo] = useState('');
    const [historyService, setHistoryService] = useState('all');
    const [historyItemType, setHistoryItemType] = useState('all');
    const [historyItemId, setHistoryItemId] = useState('');

    const selectableItems = useMemo(() => {
        const ingredientOptions = (ingredients || []).map((item) => ({
            id: item.id,
            itemType: 'ingredient',
            name: item.name,
            stock: Number(item.stock ?? item.stock_quantity ?? 0),
            unit: item.unit || 'unité',
        }));

        const consumableOptions = (consumables || []).map((item) => ({
            id: item.id,
            itemType: 'consumable',
            name: item.name,
            stock: Number(item.stock ?? item.stock_quantity ?? 0),
            unit: item.unit || 'unité',
        }));

        return [...ingredientOptions, ...consumableOptions];
    }, [ingredients, consumables]);

    const normalizeHistoryEntry = (entry) => {
        const itemName = entry?.stockable?.name || entry?.item_name || entry?.name || '—';
        const quantity = Math.abs(Number(entry?.quantity ?? 0));
        const serviceLabel = entry?.service === 'cuisine' ? 'Cuisine' : entry?.service === 'barista' ? 'Barista' : '—';
        const movementDate = entry?.movement_date || entry?.created_at || null;

        return {
            id: entry?.id,
            itemName,
            quantity,
            service: entry?.service || null,
            serviceLabel,
            movementDate,
            notes: entry?.notes || entry?.reference || '—',
            stockableType: entry?.stockable_type || null,
            stockableId: entry?.stockable_id || null,
        };
    };

    const loadHistory = async () => {
        setHistoryLoading(true);
        setHistoryError('');

        try {
            const params = new URLSearchParams();
            params.set('type', 'consumption');
            params.set('reference', 'stock-output');
            if (historyDateFrom) params.set('date_from', historyDateFrom);
            if (historyDateTo) params.set('date_to', historyDateTo);
            if (historyService !== 'all') params.set('service', historyService);
            if (historyItemType !== 'all') {
                params.set('item_type', historyItemType);
                if (historyItemId) params.set('item_id', historyItemId);
            }

            const response = await fetch(`/api/inventory/movements?${params.toString()}`, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
            });

            if (!response.ok) throw new Error('Impossible d’obtenir l’historique des sorties.');
            const payload = await response.json();
            const items = Array.isArray(payload?.data) ? payload.data : [];
            setHistoryEntries(items.map(normalizeHistoryEntry));
        } catch (loadError) {
            setHistoryError(loadError?.message || 'Impossible d’obtenir l’historique des sorties.');
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        loadHistory();
    }, [historyDateFrom, historyDateTo, historyService, historyItemType, historyItemId]);

    const updateRow = (rowId, changes) => {
        setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, ...changes } : row)));
    };

    const addRow = () => {
        setRows((prev) => [...prev, defaultRow(Date.now())]);
    };

    const removeRow = (rowId) => {
        setRows((prev) => (prev.length > 1 ? prev.filter((row) => row.id !== rowId) : prev));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');

        const normalizedRows = rows.map((row) => ({
            ...row,
            quantity: Number(row.quantity),
        }));

        const invalidRow = normalizedRows.find((row) => !row.itemId || !Number.isFinite(row.quantity) || row.quantity <= 0);
        if (invalidRow) {
            setError('Chaque ligne doit contenir un élément valide et une quantité positive.');
            return;
        }

        for (const row of normalizedRows) {
            const selectedItem = selectableItems.find((item) => item.itemType === row.itemType && item.id === Number(row.itemId));
            if (!selectedItem) {
                setError('Un élément sélectionné est introuvable.');
                return;
            }
            if (row.quantity > selectedItem.stock) {
                setError(`Stock insuffisant pour ${selectedItem.name} (${selectedItem.stock} disponible).`);
                return;
            }
        }

        try {
            setSubmitting(true);
            const createdOutput = await onCreateStockOutput({
                movement_date: movementDate,
                service,
                items: normalizedRows.map((row) => ({
                    item_type: row.itemType,
                    item_id: Number(row.itemId),
                    quantity: row.quantity,
                    notes: row.notes || '',
                })),
            });

            const newEntries = (createdOutput?.movements || []).map(normalizeHistoryEntry);
            setHistoryEntries((prev) => [...newEntries, ...prev].slice(0, 50));
            setSuccess('Sortie enregistrée avec succès.');
            setRows([defaultRow(1)]);
            setMovementDate(new Date().toISOString().slice(0, 10));
            setService('cuisine');
        } catch (submitError) {
            setError(submitError?.message || 'Impossible d’enregistrer la sortie de stock.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <section className="rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-green-400">Sortie stock principal</p>
                        <h2 className="text-3xl font-semibold text-green-900">Saisie des sorties de stock</h2>
                        <p className="mt-2 text-sm text-green-600">
                            Chaque ligne retire le stock actuel pour un ingrédient ou un consommable, avec une date et une destination précisées.
                        </p>
                    </div>
                    <div className="flex flex-col gap-3 rounded-3xl border border-green-200 bg-white p-4 shadow-sm sm:min-w-[280px]">
                        <label className="text-sm font-medium text-green-900">
                            Date de sortie
                            <input
                                type="date"
                                value={movementDate}
                                onChange={(event) => setMovementDate(event.target.value)}
                                className="mt-2 w-full rounded-2xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                            />
                        </label>
                        <label className="text-sm font-medium text-green-900">
                            Destination
                            <select
                                value={service}
                                onChange={(event) => setService(event.target.value)}
                                className="mt-2 w-full rounded-2xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                            >
                                <option value="cuisine">Cuisine</option>
                                <option value="barista">Barista</option>
                            </select>
                        </label>
                        <button
                            type="button"
                            onClick={() => { window.location.href = '/stock-trends'; }}
                            className="rounded-full border border-green-400 bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700"
                        >
                            Voir tendances de stock
                        </button>
                    </div>
                </div>
            </section>

            <section className="rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                <form onSubmit={handleSubmit} className="space-y-4">
                    {error ? (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            {error}
                        </div>
                    ) : null}
                    {success ? (
                        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                            {success}
                        </div>
                    ) : null}

                    <div className="overflow-x-auto rounded-3xl border border-green-200 bg-green-50">
                        <table className="min-w-full divide-y divide-green-200 text-left text-sm text-slate-700">
                            <thead className="bg-green-100 text-green-900">
                                <tr>
                                    <th className="px-4 py-3 font-medium">Élément</th>
                                    <th className="px-4 py-3 font-medium">Stock actuel</th>
                                    <th className="px-4 py-3 font-medium">Quantité sortie</th>
                                    <th className="px-4 py-3 font-medium">Note</th>
                                    <th className="px-4 py-3 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-green-100 bg-white">
                                {rows.map((row) => {
                                    const selectedItem = selectableItems.find((item) => item.itemType === row.itemType && item.id === Number(row.itemId));
                                    return (
                                        <tr key={row.id}>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-col gap-2">
                                                    <select
                                                        value={row.itemType}
                                                        onChange={(event) => updateRow(row.id, { itemType: event.target.value, itemId: '' })}
                                                        className="rounded-2xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                                    >
                                                        <option value="ingredient">Ingrédient</option>
                                                        <option value="consumable">Consommable</option>
                                                    </select>
                                                    <select
                                                        value={row.itemId}
                                                        onChange={(event) => updateRow(row.id, { itemId: event.target.value })}
                                                        className="rounded-2xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                                    >
                                                        <option value="">Sélectionner</option>
                                                        {selectableItems
                                                            .filter((item) => item.itemType === row.itemType)
                                                            .map((item) => (
                                                                <option key={`${item.itemType}-${item.id}`} value={item.id}>
                                                                    {item.name}
                                                                </option>
                                                            ))}
                                                    </select>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-green-900">
                                                {selectedItem ? `${selectedItem.stock} ${selectedItem.unit}` : '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <input
                                                    type="number"
                                                    min="0.001"
                                                    step="0.001"
                                                    value={row.quantity}
                                                    onChange={(event) => updateRow(row.id, { quantity: event.target.value })}
                                                    className="w-full rounded-2xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                                    placeholder="Quantité"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <input
                                                    type="text"
                                                    value={row.notes}
                                                    onChange={(event) => updateRow(row.id, { notes: event.target.value })}
                                                    className="w-full rounded-2xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                                                    placeholder="Motif / note"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <button
                                                    type="button"
                                                    onClick={() => removeRow(row.id)}
                                                    className="rounded-full border border-rose-300 px-3 py-1.5 text-sm font-semibold text-rose-600 transition hover:bg-rose-50"
                                                >
                                                    Supprimer
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <button
                            type="button"
                            onClick={addRow}
                            className="rounded-full border border-green-400 bg-white px-4 py-2 text-sm font-semibold text-green-700 transition hover:bg-green-100"
                        >
                            Ajouter une ligne
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="rounded-full bg-green-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-70"
                        >
                            {submitting ? 'Enregistrement…' : 'Enregistrer la sortie'}
                        </button>
                    </div>
                </form>
            </section>

            <section className="rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <h3 className="text-xl font-semibold text-green-900">Historique des sorties</h3>
                        <p className="text-sm text-green-600">Tri par date décroissante, avec filtre par élément et par destination.</p>
                    </div>
                    <div className="grid gap-3 md:grid-cols-4">
                        <label className="text-sm font-medium text-green-900">
                            Du
                            <input
                                type="date"
                                value={historyDateFrom}
                                onChange={(event) => setHistoryDateFrom(event.target.value)}
                                className="mt-2 w-full rounded-2xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                            />
                        </label>
                        <label className="text-sm font-medium text-green-900">
                            Au
                            <input
                                type="date"
                                value={historyDateTo}
                                onChange={(event) => setHistoryDateTo(event.target.value)}
                                className="mt-2 w-full rounded-2xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                            />
                        </label>
                        <label className="text-sm font-medium text-green-900">
                            Élément
                            <select
                                value={historyItemType}
                                onChange={(event) => {
                                    setHistoryItemType(event.target.value);
                                    setHistoryItemId('');
                                }}
                                className="mt-2 w-full rounded-2xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                            >
                                <option value="all">Tous</option>
                                <option value="ingredient">Ingrédients</option>
                                <option value="consumable">Consommables</option>
                            </select>
                        </label>
                        <label className="text-sm font-medium text-green-900">
                            Destination
                            <select
                                value={historyService}
                                onChange={(event) => setHistoryService(event.target.value)}
                                className="mt-2 w-full rounded-2xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 focus:border-green-400 focus:outline-none"
                            >
                                <option value="all">Toutes</option>
                                <option value="cuisine">Cuisine</option>
                                <option value="barista">Barista</option>
                            </select>
                        </label>
                    </div>
                </div>

                <div className="mt-4 overflow-x-auto rounded-3xl border border-green-200 bg-green-50">
                    <table className="min-w-full divide-y divide-green-200 text-left text-sm text-slate-700">
                        <thead className="bg-green-100 text-green-900">
                            <tr>
                                <th className="px-4 py-3 font-medium">Date</th>
                                <th className="px-4 py-3 font-medium">Élément</th>
                                <th className="px-4 py-3 font-medium">Quantité</th>
                                <th className="px-4 py-3 font-medium">Destination</th>
                                <th className="px-4 py-3 font-medium">Note</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-green-100 bg-white">
                            {historyLoading ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-6 text-center text-sm text-green-600">Chargement…</td>
                                </tr>
                            ) : historyError ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-6 text-center text-sm text-rose-600">{historyError}</td>
                                </tr>
                            ) : historyEntries.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-6 text-center text-sm text-green-600">Aucune sortie enregistrée pour ces filtres.</td>
                                </tr>
                            ) : (
                                historyEntries.map((entry) => (
                                    <tr key={entry.id} className="hover:bg-green-50">
                                        <td className="px-4 py-3 text-green-900">
                                            {entry.movementDate ? new Date(entry.movementDate).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                                        </td>
                                        <td className="px-4 py-3 font-medium text-slate-900">{entry.itemName}</td>
                                        <td className="px-4 py-3 text-slate-900">-{entry.quantity}</td>
                                        <td className="px-4 py-3 text-slate-900">{entry.serviceLabel}</td>
                                        <td className="px-4 py-3 text-slate-900">{entry.notes}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
